import { ReactElement, ReactNode, useMemo } from 'react'
import { find } from 'lodash-es'
import { TabContext, TabList } from '@mui/lab'
import { Tab } from '@mui/material'
import { StyledTabPanel } from '@ui/navigation'
import { Box } from '@ui/structure'
import { useStructuralPlanningSelection } from '@structuralPlanningHooks'
import VerticalTransmitterList from '../../../../VerticalTransmitterList'
import { beamTabs } from '../tabs'

interface Props {
  selectedElement: string
  children?: ReactNode
  tab?: ReactNode
  initialTabValue: string
}

const BeamFormBase = ({ children, tab, initialTabValue, selectedElement }: Props): ReactElement => {
  const {
    modes: { isDrawMode },
    params: { verticalTransmitter, activeTab },
    actions: { setVerticalTransmitter, setActiveTab },
  } = useStructuralPlanningSelection()

  const selectedTab = useMemo(() => {
    if (activeTab && find(Object.entries(beamTabs), ([, e]) => e.tab === activeTab))
      return activeTab

    return initialTabValue || beamTabs.geometry.tab
  }, [activeTab, initialTabValue])

  return (
    <TabContext value={selectedTab}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': {
            fontSize: 12,
            paddingX: ({ spacing }) => spacing(1.5),
          },
        }}
      >
        <TabList onChange={(_, value) => setActiveTab(value)} scrollButtons="auto">
          {tab}
          <Tab
            value={beamTabs.verticalTransmission.tab}
            label={beamTabs.verticalTransmission.label}
            data-cy="tab-vertical-transmission"
          />
        </TabList>
      </Box>
      {children}
      <StyledTabPanel value={beamTabs.verticalTransmission.tab}>
        <VerticalTransmitterList
          selectedElement={selectedElement}
          verticalTransmitter={verticalTransmitter}
          setVerticalTransmitter={(guid, elementGuid) =>
            setVerticalTransmitter(guid, elementGuid, isDrawMode)
          }
        />
      </StyledTabPanel>
    </TabContext>
  )
}

export default BeamFormBase
